import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import ExportModal from "@/components/ExportModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Search, 
  Phone,
  Download,
  Radio,
  FileText,
  X,
  Activity,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Filter
} from "lucide-react";
import { format } from "date-fns";

export default function Dispatch({ embedded = false }) {
  const navigate = useNavigate();
  const [dispatchers, setDispatchers] = useState([]);
  const [selectedDispatcher, setSelectedDispatcher] = useState(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportData, setExportData] = useState("");
  const [exportFilename, setExportFilename] = useState("");
  const [exportDownloadUrl, setExportDownloadUrl] = useState("");
  const [dispatchIncidents, setDispatchIncidents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [incidentTypes, setIncidentTypes] = useState([]);
  
  // Main tab
  const [mainTab, setMainTab] = useState("dispatchers");
  
  // Audit tab state
  const [auditData, setAuditData] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditStartDate, setAuditStartDate] = useState("");
  const [auditEndDate, setAuditEndDate] = useState("");
  const [auditIncidentSearch, setAuditIncidentSearch] = useState("");
  const [auditAddressSearch, setAuditAddressSearch] = useState("");
  const [auditTypeFilter, setAuditTypeFilter] = useState("all");
  const [auditDispatcherFilter, setAuditDispatcherFilter] = useState("all");
  const [expandedDispatchers, setExpandedDispatchers] = useState({});
  
  // Detail panel filters
  const [detailStartDate, setDetailStartDate] = useState("");
  const [detailEndDate, setDetailEndDate] = useState("");
  const [detailTypeFilter, setDetailTypeFilter] = useState("all");
  const [detailSearch, setDetailSearch] = useState("");

  useEffect(() => {
    fetchDispatchers();
    fetchIncidentTypes();
  }, []);

  const fetchDispatchers = async () => {
    try {
      const response = await axios.get(`${API}/units/dispatchers`, { withCredentials: true });
      setDispatchers(response.data);
    } catch (error) {
      toast.error("Failed to load dispatchers");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchIncidentTypes = async () => {
    try {
      const response = await axios.get(`${API}/incident-types`, { withCredentials: true });
      setIncidentTypes(response.data || []);
    } catch (error) {}
  };

  const fetchDispatchIncidents = async (dispatcherName) => {
    try {
      const response = await axios.get(`${API}/incidents?dispatcher=${dispatcherName}`, { withCredentials: true });
      setDispatchIncidents(response.data);
    } catch (error) {
      console.error("Failed to fetch dispatch incidents:", error);
    }
  };
  
  const fetchAuditData = async () => {
    try {
      setAuditLoading(true);
      const params = new URLSearchParams();
      if (auditStartDate) params.append("start_date", auditStartDate);
      if (auditEndDate) params.append("end_date", auditEndDate);
      if (auditIncidentSearch) params.append("incident_search", auditIncidentSearch);
      if (auditAddressSearch) params.append("address_search", auditAddressSearch);
      if (auditTypeFilter !== "all") params.append("incident_type", auditTypeFilter);
      if (auditDispatcherFilter !== "all") params.append("dispatcher_name", auditDispatcherFilter);
      
      const response = await axios.get(`${API}/dispatch/audit?${params}`, { withCredentials: true });
      setAuditData(response.data || []);
    } catch (error) {
      toast.error("Failed to load audit data");
    } finally {
      setAuditLoading(false);
    }
  };

  const handleSelectDispatcher = (dispatcher) => {
    setSelectedDispatcher(dispatcher);
    fetchDispatchIncidents(dispatcher.name);
  };

  const handleExport = async () => {
    if (!selectedDispatcher) return;
    try {
      toast.info("Preparing export...");
      const params = new URLSearchParams();
      if (detailStartDate) params.append("start_date", detailStartDate);
      if (detailEndDate) params.append("end_date", detailEndDate);
      if (detailTypeFilter !== "all") params.append("incident_type", detailTypeFilter);
      if (detailSearch) params.append("search", detailSearch);
      
      const response = await axios.get(
        `${API}/export/dispatch/${selectedDispatcher.name}?${params}`,
        { withCredentials: true, responseType: "text" }
      );
      if (!response.data || response.data.includes('"message"')) {
        toast.info("No data to export");
        return;
      }
      const filename = `dispatch_${selectedDispatcher.name}_${format(new Date(), "yyyy-MM-dd")}.csv`;
      const downloadUrl = `${API}/download/dispatch/${selectedDispatcher.name}?${params}`;
      
      setExportData(response.data);
      setExportFilename(filename);
      setExportDownloadUrl(downloadUrl);
      setShowExportDialog(true);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Export failed");
    }
  };
  
  const handleAuditExport = async () => {
    try {
      toast.info("Preparing audit export...");
      const params = new URLSearchParams();
      if (auditStartDate) params.append("start_date", auditStartDate);
      if (auditEndDate) params.append("end_date", auditEndDate);
      if (auditIncidentSearch) params.append("incident_search", auditIncidentSearch);
      if (auditAddressSearch) params.append("address_search", auditAddressSearch);
      if (auditTypeFilter !== "all") params.append("incident_type", auditTypeFilter);
      if (auditDispatcherFilter !== "all") params.append("dispatcher_name", auditDispatcherFilter);
      
      const response = await axios.get(`${API}/download/dispatch-audit?${params}`, { withCredentials: true, responseType: "text" });
      if (!response.data) { toast.info("No data to export"); return; }
      setExportData(response.data);
      setExportFilename(`dispatch_audit_${format(new Date(), "yyyy-MM-dd")}.csv`);
      setExportDownloadUrl(`${API}/download/dispatch-audit?${params}`);
      setShowExportDialog(true);
    } catch (error) {
      toast.error("Export failed");
    }
  };
  
  // Filter detail incidents
  const filteredDetailIncidents = dispatchIncidents.filter(inc => {
    if (detailStartDate && inc.date_time < detailStartDate) return false;
    if (detailEndDate && inc.date_time > detailEndDate + "T23:59:59") return false;
    if (detailTypeFilter !== "all" && inc.incident_type !== detailTypeFilter) return false;
    if (detailSearch) {
      const search = detailSearch.toLowerCase();
      if (!inc.headline?.toLowerCase().includes(search) && 
          !inc.incident_id?.toLowerCase().includes(search) &&
          !inc.address?.toLowerCase().includes(search)) return false;
    }
    return true;
  });

  const filteredDispatchers = dispatchers.filter(d =>
    d.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const toggleDispatcherExpand = (dispatcherName) => {
    setExpandedDispatchers(prev => ({
      ...prev,
      [dispatcherName]: !prev[dispatcherName]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className={embedded ? "h-full" : "h-[calc(100vh-8rem)]"} data-testid="dispatch-page">
      {/* Header - hidden when embedded */}
      {!embedded && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <Phone className="w-6 h-6 text-blue-500" />
            <h1 className="text-xl sm:text-2xl font-bold uppercase tracking-tight">Dispatch</h1>
            <Badge variant="outline">{dispatchers.length} Units</Badge>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex bg-slate-800 rounded p-0.5 mr-2">
              <Button variant={mainTab === "dispatchers" ? "default" : "ghost"} size="sm" onClick={() => setMainTab("dispatchers")} className={mainTab === "dispatchers" ? "bg-blue-600" : ""}>
                <Radio className="w-4 h-4 mr-1" />Dispatchers
              </Button>
              <Button variant={mainTab === "audit" ? "default" : "ghost"} size="sm" onClick={() => { setMainTab("audit"); if (auditData.length === 0) fetchAuditData(); }} className={mainTab === "audit" ? "bg-blue-600" : ""}>
                <Activity className="w-4 h-4 mr-1" />Audit
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Embedded header with just tabs */}
      {embedded && (
        <div className="flex items-center gap-2 mb-4">
          <Badge variant="outline">{dispatchers.length} Dispatcher Units</Badge>
          <div className="flex bg-slate-800 rounded p-0.5 ml-auto">
            <Button variant={mainTab === "dispatchers" ? "default" : "ghost"} size="sm" onClick={() => setMainTab("dispatchers")} className={mainTab === "dispatchers" ? "bg-blue-600" : ""}>
              <Radio className="w-4 h-4 mr-1" />Dispatchers
            </Button>
            <Button variant={mainTab === "audit" ? "default" : "ghost"} size="sm" onClick={() => { setMainTab("audit"); if (auditData.length === 0) fetchAuditData(); }} className={mainTab === "audit" ? "bg-blue-600" : ""}>
              <Activity className="w-4 h-4 mr-1" />Audit
            </Button>
          </div>
        </div>
      )}

      {mainTab === "dispatchers" ? (
        /* Dispatchers Tab */
        <div className="flex flex-col lg:flex-row h-full gap-4">
          {/* Left Panel - Dispatcher List */}
          <div className={`w-full lg:w-1/3 bg-slate-900 border border-slate-800 rounded-sm flex flex-col ${selectedDispatcher ? 'hidden lg:flex' : ''}`}>
            <div className="p-3 border-b border-slate-800">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search dispatchers..."
                  className="pl-10 input-dark"
                  data-testid="search-dispatchers"
                />
              </div>
            </div>
            <ScrollArea className="flex-1 p-2">
              {filteredDispatchers.map((dispatcher) => (
                <div
                  key={dispatcher.unit_id}
                  onClick={() => handleSelectDispatcher(dispatcher)}
                  data-testid={`dispatcher-${dispatcher.name}`}
                  className={`p-4 rounded-sm mb-1 cursor-pointer flex items-center gap-3 ${
                    selectedDispatcher?.unit_id === dispatcher.unit_id
                      ? "bg-blue-600/20 border border-blue-500/50"
                      : "hover:bg-slate-800"
                  }`}
                >
                  <div className="w-10 h-10 rounded-sm bg-amber-900/30 flex items-center justify-center">
                    <Radio className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <span className="font-mono font-bold text-lg">{dispatcher.name}</span>
                    <p className="text-xs text-slate-500">Dispatcher Unit</p>
                  </div>
                </div>
              ))}
            </ScrollArea>
          </div>

          {/* Right Panel - Dispatcher Details */}
          <div className={`w-full lg:w-2/3 bg-slate-900 border border-slate-800 rounded-sm ${selectedDispatcher ? 'block' : 'hidden lg:block'}`}>
            {selectedDispatcher ? (
              <div className="p-4 h-full flex flex-col">
                {/* Mobile Back Button */}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="lg:hidden mb-3 w-fit"
                  onClick={() => setSelectedDispatcher(null)}
                >
                  <X className="w-4 h-4 mr-1" />
                  Back to List
                </Button>

                {/* Dispatcher Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-4 border-b border-slate-800">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-sm bg-amber-900/30 flex items-center justify-center">
                      <Radio className="w-6 h-6 sm:w-7 sm:h-7 text-amber-400" />
                    </div>
                    <div>
                      <h2 className="text-xl sm:text-2xl font-bold font-mono">{selectedDispatcher.name}</h2>
                      <p className="text-slate-400 text-sm">Dispatcher Unit</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-dispatch">
                    <Download className="w-4 h-4 mr-1" />
                    Export ({filteredDetailIncidents.length})
                  </Button>
                </div>

                {/* Stats */}
                <Card className="bg-slate-950 border-slate-800 mb-4">
                  <CardContent className="p-3 sm:p-4 flex items-center justify-between">
                    <div>
                      <p className="text-3xl sm:text-4xl font-bold text-amber-400">{filteredDetailIncidents.length}</p>
                      <p className="text-xs sm:text-sm text-slate-500 uppercase">Incidents</p>
                    </div>
                    <FileText className="w-10 h-10 text-amber-500/30" />
                  </CardContent>
                </Card>
                
                {/* Filters */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <Input type="date" value={detailStartDate} onChange={(e) => setDetailStartDate(e.target.value)} className="input-dark h-8 w-32 text-xs" placeholder="From" />
                  <Input type="date" value={detailEndDate} onChange={(e) => setDetailEndDate(e.target.value)} className="input-dark h-8 w-32 text-xs" placeholder="To" />
                  <Select value={detailTypeFilter} onValueChange={setDetailTypeFilter}>
                    <SelectTrigger className="input-dark h-8 w-32 text-xs"><SelectValue placeholder="Type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {incidentTypes.map(t => <SelectItem key={t.type_id} value={t.name}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="relative flex-1 min-w-[150px]">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-slate-500" />
                    <Input value={detailSearch} onChange={(e) => setDetailSearch(e.target.value)} placeholder="Search..." className="input-dark h-8 pl-7 text-xs" />
                  </div>
                  <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setDetailStartDate(""); setDetailEndDate(""); setDetailTypeFilter("all"); setDetailSearch(""); }}>
                    <X className="w-3 h-3 mr-1" />Clear
                  </Button>
                </div>

                {/* Incident List */}
                <div className="flex-1 overflow-hidden">
                  <h3 className="form-section-title mb-2">Dispatched Incidents</h3>
                  <Card className="bg-slate-950 border-slate-800">
                    <ScrollArea className="h-80">
                      <CardContent className="p-2">
                        {filteredDetailIncidents.length === 0 ? (
                          <p className="text-center text-slate-500 py-8">No incidents found</p>
                        ) : (
                          filteredDetailIncidents.map((inc) => (
                            <div 
                              key={inc.incident_id} 
                              className="p-3 border-b border-slate-800 last:border-0 hover:bg-slate-800/50 cursor-pointer"
                              onClick={() => navigate(`/incidents/${inc.incident_id}`)}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <p className="font-medium truncate">{inc.headline}</p>
                                <Badge variant="outline" className="text-xs">
                                  {inc.incident_type}
                                </Badge>
                              </div>
                              <div className="flex items-center justify-between text-xs text-slate-500">
                                <span className="font-mono">{inc.incident_id}</span>
                                <span>{format(new Date(inc.date_time), "MM/dd/yy HH:mm")}</span>
                              </div>
                            </div>
                          ))
                        )}
                      </CardContent>
                    </ScrollArea>
                  </Card>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-500">
                <div className="text-center">
                  <Phone className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Select a dispatcher to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Audit Tab */
        <div className="h-full flex flex-col">
          {/* Audit Filters */}
          <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-slate-900 rounded border border-slate-800">
            <Select value={auditDispatcherFilter} onValueChange={setAuditDispatcherFilter}>
              <SelectTrigger className="input-dark h-8 w-36 text-xs"><SelectValue placeholder="Dispatcher" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Dispatchers</SelectItem>
                {dispatchers.map(d => <SelectItem key={d.unit_id} value={d.name}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-slate-500" />
              <Input value={auditIncidentSearch} onChange={(e) => setAuditIncidentSearch(e.target.value)} placeholder="Incident headline or ID..." className="pl-7 input-dark h-8 w-44 text-xs" onKeyDown={(e) => e.key === "Enter" && fetchAuditData()} />
            </div>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-slate-500" />
              <Input value={auditAddressSearch} onChange={(e) => setAuditAddressSearch(e.target.value)} placeholder="Address..." className="pl-7 input-dark h-8 w-36 text-xs" onKeyDown={(e) => e.key === "Enter" && fetchAuditData()} />
            </div>
            <Select value={auditTypeFilter} onValueChange={setAuditTypeFilter}>
              <SelectTrigger className="input-dark h-8 w-32 text-xs"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {incidentTypes.map(t => <SelectItem key={t.type_id} value={t.name}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" value={auditStartDate} onChange={(e) => setAuditStartDate(e.target.value)} className="input-dark h-8 w-32 text-xs" />
            <span className="text-slate-500 text-xs">to</span>
            <Input type="date" value={auditEndDate} onChange={(e) => setAuditEndDate(e.target.value)} className="input-dark h-8 w-32 text-xs" />
            <Button onClick={fetchAuditData} disabled={auditLoading} className="btn-primary h-8 text-xs">
              <Filter className="w-3 h-3 mr-1" />{auditLoading ? "Loading..." : "Apply"}
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => { setAuditStartDate(""); setAuditEndDate(""); setAuditIncidentSearch(""); setAuditAddressSearch(""); setAuditTypeFilter("all"); setAuditDispatcherFilter("all"); }}>
              <X className="w-3 h-3 mr-1" />Clear
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleAuditExport} disabled={auditData.length === 0}>
              <Download className="w-3 h-3 mr-1" />Export
            </Button>
          </div>
          
          {/* Active filter badges */}
          <div className="flex flex-wrap gap-1 mb-3">
            {auditDispatcherFilter !== "all" && <Badge variant="secondary" className="text-xs">Dispatcher: {auditDispatcherFilter} <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditDispatcherFilter("all")} /></Badge>}
            {auditTypeFilter !== "all" && <Badge variant="secondary" className="text-xs">Type: {auditTypeFilter} <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditTypeFilter("all")} /></Badge>}
            {auditIncidentSearch && <Badge variant="secondary" className="text-xs">Headline: "{auditIncidentSearch}" <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditIncidentSearch("")} /></Badge>}
            {auditAddressSearch && <Badge variant="secondary" className="text-xs">Address: "{auditAddressSearch}" <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditAddressSearch("")} /></Badge>}
            {auditStartDate && <Badge variant="secondary" className="text-xs">From: {auditStartDate} <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditStartDate("")} /></Badge>}
            {auditEndDate && <Badge variant="secondary" className="text-xs">To: {auditEndDate} <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditEndDate("")} /></Badge>}
          </div>
          
          {/* Audit Results */}
          <ScrollArea className="flex-1">
            {auditLoading ? <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div></div> : auditData.length === 0 ? <div className="text-center py-20 text-slate-500"><Activity className="w-12 h-12 mx-auto mb-4 opacity-30" /><p>Click "Apply" to load audit data</p></div> : (
              <div className="space-y-2">
                {auditData.map((dispatcher) => (
                  <Card key={dispatcher.dispatcher_name} className="bg-slate-900 border-slate-800">
                    <CardHeader className="p-3 cursor-pointer hover:bg-slate-800/50" onClick={() => toggleDispatcherExpand(dispatcher.dispatcher_name)}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {expandedDispatchers[dispatcher.dispatcher_name] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          <Radio className="w-4 h-4 text-amber-400" />
                          <span className="font-mono font-bold">{dispatcher.dispatcher_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs">{dispatcher.incidents_dispatched?.length || 0} incidents</Badge>
                        </div>
                      </div>
                    </CardHeader>
                    {expandedDispatchers[dispatcher.dispatcher_name] && (
                      <CardContent className="p-3 pt-0 border-t border-slate-800">
                        <div className="space-y-1 max-h-60 overflow-y-auto">
                          {(dispatcher.incidents_dispatched || []).map((inc) => (
                            <div key={inc.incident_id} className="flex items-center justify-between p-2 bg-slate-800/50 rounded text-xs hover:bg-slate-800 cursor-pointer" onClick={() => navigate(`/incidents/${inc.incident_id}`)}>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{inc.headline}</p>
                                <p className="text-slate-500 truncate">{inc.address}</p>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                <Badge variant="outline" className="text-[10px]">{inc.incident_type}</Badge>
                                <span className="text-slate-500 font-mono">{inc.date_time ? format(new Date(inc.date_time), "MM/dd HH:mm") : ""}</span>
                                <ExternalLink className="w-3 h-3 text-blue-400" />
                              </div>
                            </div>
                          ))}
                          {(!dispatcher.incidents_dispatched || dispatcher.incidents_dispatched.length === 0) && (
                            <p className="text-slate-500 text-center py-4">No incidents dispatched</p>
                          )}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      )}
      
      {/* Export Modal */}
      <ExportModal
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        csvData={exportData}
        filename={exportFilename}
        downloadUrl={exportDownloadUrl}
      />
    </div>
  );
}
